import { useStorage } from '@vueuse/core'
import { router } from '@/plugins/1.router/index'
import navigation from '@/navigation/vertical'

interface NavItem {
  title?: string
  to?: string | { path?: string, name?: string }
  href?: string
  children?: NavItem[]
}

interface PendingAction {
  id: string
  action: string
  at: number
  formNo: string
}

const SKIP_ACTION = /查詢|搜尋|匯出|下載|登入|excel|xlsx/i
const DOCUMENT_NO = /^[A-Za-z]{1,12}-?\d{4,}[A-Za-z0-9-]*$/
const DETAIL_NO = /-D\d+$/
const ACTION_TTL_MS = 5 * 60 * 1000

const byPath = new Map<string, string>()
const byName = new Map<string, string>()
let pending: PendingAction | null = null
let installed = false
const impersonationSnapshot = useStorage<unknown>('impersonationOriginal', null)

function impersonating() {
  return impersonationSnapshot.value != null
}

function remember(title: string | undefined, path?: string, name?: string) {
  const label = title?.trim()
  if (!label)
    return
  if (path)
    byPath.set(path, label)
  if (name)
    byName.set(name, label)
}

function indexNav(items: NavItem[]) {
  items?.forEach(item => {
    if (typeof item.to === 'string')
      remember(item.title, undefined, item.to)
    else if (item.to)
      remember(item.title, item.to.path, item.to.name)
    if (item.href) {
      try {
        remember(item.title, new URL(item.href, window.location.origin).pathname)
      }
      catch {
        // 略過無法解析的外部連結
      }
    }
    if (item.children?.length)
      indexNav(item.children)
  })
}

indexNav((navigation ?? []) as NavItem[])

function pageName() {
  const route = router.currentRoute.value
  const path = route.path || ''
  const name = route.name == null ? '' : String(route.name)
  return byPath.get(path) || byName.get(name) || ''
}

function cleanFormNo(value: string) {
  const text = value.replace(/\s+/g, '')
  if (!text || text.length > 40)
    return ''
  if (/尚未|未產生|未建立|新單據/.test(text))
    return ''
  if (DETAIL_NO.test(text))
    return ''
  return DOCUMENT_NO.test(text) ? text : ''
}

function isFormNoLabel(label: string) {
  const text = label.replace(/\s+/g, '')
  if (text.includes('原') || text.length > 8)
    return false
  if (text.endsWith('單號'))
    return true
  return text === '單據編號' || (text.endsWith('編號') && text.includes('單'))
}

function formNoFromPage() {
  const labels = document.querySelectorAll('.ai-info-label, .v-list-item-title, label')
  for (const label of labels) {
    if (!isFormNoLabel(label.textContent || ''))
      continue
    const container = label.closest('.ai-info-item, .v-list-item, .v-col') || label.parentElement
    const value = container?.querySelector('.ai-info-value-prikey, .ai-info-value-generic, .v-list-item-subtitle')?.textContent || ''
    const formNo = cleanFormNo(value)
    if (formNo)
      return formNo
  }
  const query = router.currentRoute.value.query
  const fromQuery = query.applicationNo || query.priKey || query.formNo || query.id
  const queryValue = Array.isArray(fromQuery) ? fromQuery[0] : fromQuery
  return cleanFormNo(String(queryValue || ''))
}

function formNoFromRow(target: Element) {
  const row = target.closest('tr, .list-card, .v-list-item')
  if (!row)
    return ''
  const matched = row.textContent?.match(/[A-Za-z]{1,12}-?\d{4,}[A-Za-z0-9-]*/)
  return cleanFormNo(matched?.[0] || '')
}

function buttonText(target: Element) {
  const button = target.closest('button, .v-btn, [role="button"]')
  if (!button)
    return ''
  return (button.textContent || '').replace(/\s+/g, ' ').trim()
}

function onClick(event: Event) {
  if (!impersonating())
    return
  const target = event.target
  if (!(target instanceof Element))
    return
  const text = buttonText(target)
  if (!text || text.length > 20)
    return
  const inDialog = !!target.closest('.swal2-container, .v-overlay__content')
  if (inDialog && pending && Date.now() - pending.at < ACTION_TTL_MS) {
    pending = { ...pending, at: Date.now() }
    return
  }
  if (SKIP_ACTION.test(text.replace(/\s/g, ''))) {
    pending = null
    return
  }
  pending = {
    id: crypto.randomUUID(),
    action: text,
    at: Date.now(),
    formNo: formNoFromRow(target),
  }
}

export function installImpersonationAudit() {
  if (installed || typeof document === 'undefined')
    return
  installed = true
  document.addEventListener('click', onClick, true)
}

function skipRequest(method: string, url: string) {
  const verb = method.toUpperCase()
  if (!['POST', 'PUT', 'PATCH', 'DELETE'].includes(verb))
    return true
  const lower = url.toLowerCase()
  return lower.includes('/sys/impersonation')
    || lower.includes('export')
    || lower.includes('excel')
    || lower.includes('xlsx')
    || lower.includes('download')
}

function requestUrl(request: unknown) {
  if (typeof request === 'string')
    return request
  if (request && typeof request === 'object' && 'url' in request)
    return String((request as { url?: string }).url || '')
  return ''
}

export function buildImpersonationAuditHeader(method: string, request: unknown) {
  const url = requestUrl(request)
  if (!impersonating() || !pending || skipRequest(method, url))
    return ''
  if (Date.now() - pending.at > ACTION_TTL_MS) {
    pending = null
    return ''
  }
  const formNo = formNoFromPage() || pending.formNo
  return encodeURIComponent(JSON.stringify({
    id: pending.id,
    page: pageName(),
    path: router.currentRoute.value.path || '',
    action: pending.action,
    formNo,
  }))
}
